import { ExclamationCircleIcon } from '@heroicons/react/16/solid';
import { format, isAfter, parseISO } from 'date-fns';
import { isNil, some } from 'lodash';
import { Link } from 'hotel/navigation';

import ErrorMessage from 'components/ErrorMessage';
import { useFindAssignmentsByGuestAndDateQuery } from 'generated/graphql';
import buildClientTeamMemberDisplayName from 'utilities/buildClientTeamMemberDisplayName';
import { pluralize } from 'utilities/pluralize';

type Props = {
  newStayInfo: {
    guestId?: string | null;
    checkInDate?: string | null;
    checkOutDate?: string | null;
  };
  rosterItem?: {
    id: string;
    guest: {
      id: string;
      firstName: string;
      lastName: string;
    };
  };
  className?: string;
};

export default function OverlappingStayWarning({
  newStayInfo,
  rosterItem,
  className,
}: Props) {
  const { guestId, checkInDate, checkOutDate } = newStayInfo;
  const checkInDateParsed = checkInDate && parseISO(checkInDate);
  const checkOutDateParsed = checkOutDate && parseISO(checkOutDate);

  const validDateOrder =
    checkInDateParsed &&
    checkOutDateParsed &&
    isAfter(checkOutDateParsed, checkInDateParsed);
  const { data: overlapData, error: overlapError } =
    useFindAssignmentsByGuestAndDateQuery({
      variables: {
        guestId: guestId ?? '',
        checkInDate: checkInDate ?? '',
        checkOutDate: checkOutDate ?? '',
      },
      skip: some(newStayInfo, isNil) || !validDateOrder,
    });

  // filter out the related roster item if it exists
  const overlappingRequests =
    (rosterItem
      ? overlapData?.findAssignmentsByGuestAndDate?.filter(
          ({ id }) => id !== rosterItem.id,
        )
      : overlapData?.findAssignmentsByGuestAndDate) ?? [];

  if (overlappingRequests.length > 0 || overlapError) {
    if (overlapError) {
      return (
        <div className={className}>
          <ErrorMessage errorMessage="Failed to check for overlapping stays" />
        </div>
      );
    }

    return (
      <div className={`rounded-md border bg-yellow-50 p-4 ${className}`}>
        <div className="flex self-start">
          <div className="shrink-0">
            <ExclamationCircleIcon className="size-5 text-yellow-500" />
          </div>
          <div className="ml-3">
            <h3 className="mb-2 font-medium text-yellow-700">
              There {overlappingRequests.length === 1 ? 'is' : 'are'}{' '}
              {overlappingRequests.length} booked{' '}
              {pluralize(overlappingRequests.length, 'stay')} for{' '}
              {rosterItem
                ? `(${buildClientTeamMemberDisplayName(rosterItem.guest)})`
                : 'this guest'}{' '}
              conflicting with these dates
            </h3>
            <ul className="list-inside list-disc text-sm font-medium text-yellow-700">
              {overlappingRequests.map((overlappingRequestData) => {
                return (
                  <li key={overlappingRequestData.id}>
                    {format(
                      parseISO(overlappingRequestData.checkInDate),
                      'M/d/yyyy',
                    )}
                    {' - '}
                    {format(
                      parseISO(overlappingRequestData.checkOutDate),
                      'M/d/yyyy',
                    )}
                    <Link
                      to={`/roster?filters=id=${overlappingRequestData.id}`}
                      className="ml-2 text-secondaryBlue underline"
                    >
                      View this roster entry
                    </Link>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      </div>
    );
  }

  return null;
}
